import { T } from '@angular/cdk/keycodes';
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, finalize, flatMap, mergeMap, Subject, takeUntil } from 'rxjs';
import { ValidateOpkraevningsintervalResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/validate-opkraevningsinterval-response';
import { ServiceAftaleService } from 'src/app/api/services';
import { OpkraevningsDialogData, OpkraevningsDialogResponse } from 'src/app/components/aftale/models';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
    selector: 'app-opkraevnings-interval-dialog',
    templateUrl: './opkraevnings-interval-dialog.component.html',
    styleUrls: ['./opkraevnings-interval-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OpkraevningsIntervalDialogComponent implements OnInit, OnDestroy {
  constructor(
    private serviceaftaleService: ServiceAftaleService,
    private loader: LoaderService,
    private fb: UntypedFormBuilder,
    private dialog: MatDialogRef<OpkraevningsIntervalDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OpkraevningsDialogData
  ) {}

  unsubscribe$ = new Subject<void>();

  minOpkastningsinterval: number = 1;
  maxOpkastningsinterval: number = 36;

  validationErrors: string[];

  form: UntypedFormGroup;

  get opkraevningsinterval() {
    return this.form.get('opkraevningsinterval');
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      opkraevningsinterval: [
        this.data.opkraevningsInterval,
        [Validators.required, Validators.min(this.minOpkastningsinterval), Validators.max(this.maxOpkastningsinterval)],
      ],
    });

    this.loader.show();
    this.serviceaftaleService
      .validateOpkraevningsinterval({
        body: {
          opkraevningsinterval: this.data.opkraevningsInterval,
          opkraevningDate: this.data.opkraevningDate.toOkDateString(),
          serviceaftaleId: this.data.serviceaftale?.id,
          changeEffect: this.data.changeEffect,
        },
        caller: EGIS_SERVICE_CALLER,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe((response: ValidateOpkraevningsintervalResponse) => {
        if (response.result.isValid) {
          this.minOpkastningsinterval = response.minimumNytOpkraevningsinterval;
          this.maxOpkastningsinterval = response.maximumNytOpkraevningsinterval;
          this.opkraevningsinterval.setValidators(Validators.min(this.minOpkastningsinterval));
          this.opkraevningsinterval.setValidators(Validators.max(this.maxOpkastningsinterval));
        } else {
          this.opkraevningsinterval.setErrors({ invalid: true });
          this.validationErrors = response.result.errors.map((o) => o.errorMessage);
        }
      });

    this.opkraevningsinterval.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
        flatMap((val: number) => {
          this.validationErrors = [];
          return this.serviceaftaleService.validateOpkraevningsinterval({
            caller: EGIS_SERVICE_CALLER,
            body: {
              changeEffect: this.data.changeEffect,
              opkraevningsinterval: val,
              opkraevningDate: this.data.opkraevningDate.toOkDateString(),
              serviceaftaleId: this.data.serviceaftale?.id,
            },
          });
        })
      )
      .subscribe((response: ValidateOpkraevningsintervalResponse) => {
        if (response.result.isValid) {
          this.minOpkastningsinterval = response.minimumNytOpkraevningsinterval;
          this.maxOpkastningsinterval = response.maximumNytOpkraevningsinterval;
          this.opkraevningsinterval.setValidators(Validators.min(this.minOpkastningsinterval));
          this.opkraevningsinterval.setValidators(Validators.max(this.maxOpkastningsinterval));
        } else {
          this.opkraevningsinterval.setErrors({ invalid: true });
          this.validationErrors = response.result.errors.map((o) => o.errorMessage);
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  cancel() {
    this.dialog.close(null);
  }

  save() {
    const response: OpkraevningsDialogResponse = {
      opkraevningsInterval: this.opkraevningsinterval.value,
      changeEffect: this.data.changeEffect,
    };
    this.dialog.close(response);
  }
}
